import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

export interface WarehouseMultiSelectOption {
  value: string;
  label: string;
}

interface WarehouseMultiSelectFilterProps {
  label: string;
  placeholder: string;
  options: WarehouseMultiSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  className?: string;
  selectAllLabel?: string;
  clearLabel?: string;
  noneSelectedLabel?: string;
  selectedCountLabel?: (count: number) => string;
}

function buildTriggerLabel(
  options: WarehouseMultiSelectOption[],
  selectedValues: string[],
  placeholder: string,
  noneSelectedLabel: string,
  selectedCountLabel: (count: number) => string,
): string {
  if (options.length === 0) {
    return placeholder;
  }
  if (selectedValues.length === 0) {
    return noneSelectedLabel;
  }
  if (selectedValues.length >= options.length) {
    return placeholder;
  }
  if (selectedValues.length === 1) {
    const option = options.find(item => item.value === selectedValues[0]);
    return option?.label ?? selectedValues[0];
  }
  return selectedCountLabel(selectedValues.length);
}

export function WarehouseMultiSelectFilter({
  label,
  placeholder,
  options,
  selectedValues,
  onChange,
  className = "",
  selectAllLabel = "Select all",
  clearLabel = "Clear",
  noneSelectedLabel = "None selected",
  selectedCountLabel = count => `${count} selected`,
}: WarehouseMultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const allSelected = options.length > 0 && selectedValues.length >= options.length;
  const noneSelected = options.length > 0 && selectedValues.length === 0;
  const triggerLabel = useMemo(
    () =>
      buildTriggerLabel(
        options,
        selectedValues,
        placeholder,
        noneSelectedLabel,
        selectedCountLabel,
      ),
    [noneSelectedLabel, options, placeholder, selectedCountLabel, selectedValues],
  );

  const toggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(item => item !== value));
      return;
    }
    onChange([...selectedValues, value]);
  };

  const selectAll = () => onChange(options.map(option => option.value));
  const clearAll = () => onChange([]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen(current => !current)}
        className={`form-input !mt-0 flex h-10 min-h-10 max-h-10 shrink-0 items-center justify-between gap-2 cursor-pointer text-left py-1.5 text-sm leading-snug ${
          open ? "!border-indigo-500 !ring-indigo-500/30" : ""
        } ${noneSelected ? "text-amber-700" : allSelected ? "text-gray-500" : "text-gray-900"}`}
      >
        <span className="truncate">{triggerLabel}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[240px] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-3 py-2">
            <button
              type="button"
              onClick={selectAll}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
            >
              {selectAllLabel}
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              {clearLabel}
            </button>
          </div>

          <ul className="max-h-60 overflow-y-auto py-1" role="listbox" aria-multiselectable="true">
            {options.map(option => {
              const checked = selectedValues.includes(option.value);

              return (
                <li key={option.value} role="option" aria-selected={checked}>
                  <button
                    type="button"
                    onClick={() => toggleOption(option.value)}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors hover:bg-gray-50 ${
                      checked ? "bg-indigo-50/60 text-indigo-950" : "text-gray-700"
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                        checked
                          ? "border-indigo-600 bg-indigo-600 text-white"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                    <span className="truncate">{option.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
